// assets/radio-configurator.js
import { RADIO_CONFIG } from './radio-configurator-data.js';

/* ===========================
   Utilities
   =========================== */
const qs = (sel, root = document) => root.querySelector(sel);

function normalizeUrl(u) {
  return String(u || '').trim();
}

/* ===========================
   OFCOM LICENCE NOTICE (shows once)
   =========================== */
function isOfcomLicenceUrl(url) {
  const u = String(url || '');
  return u.includes('/products/ofcom-uk-general-licence') || u.includes('ofcom-uk-general-licence');
}

function showOfcomNoticeOnce() {
  const key = 'ofcom_notice_shown';
  if (sessionStorage.getItem(key)) return;

  // Prefer showing inside the widget status areas
  const msg = 'ℹ️ One OFCOM licence covers all radios on this order.';

  const status1 = document.getElementById('rcResultsStatus');
  const status2 = document.getElementById('rcStatus');

  const target = status1 || status2;
  if (target) {
    target.textContent = msg;

    // auto-clear after a few seconds
    window.setTimeout(() => {
      // only clear if it still shows the same msg (avoid clearing other statuses)
      if (target.textContent === msg) target.textContent = '';
    }, 6000);
  } else {
    // fallback
    alert(msg);
  }

  sessionStorage.setItem(key, '1');
}

function parseVariantFromUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    const v = u.searchParams.get('variant');
    return v ? String(v) : null;
  } catch (e) {
    // fallback for relative/odd strings
    const m = String(url).match(/[?&]variant=(\d+)/);
    return m ? m[1] : null;
  }
}

function getHandleFromUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    const parts = u.pathname.split('/').filter(Boolean);
    const i = parts.indexOf('products');
    return i >= 0 ? parts[i + 1] : null;
  } catch (e) {
    const parts = String(url || '').split('/').filter(Boolean);
    const i = parts.indexOf('products');
    return i >= 0 ? parts[i + 1] : null;
  }
}

function moneyFromCents(cents) {
  const c = Number(cents);
  if (!Number.isFinite(c)) return '';
  // Shopify returns price in cents (integer)
  // Use Intl with GBP; if your store is set differently it still formats nicely.
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(c / 100);
  } catch (e) {
    return `£${(c / 100).toFixed(2)}`;
  }
}

function base64UrlEncode(str) {
  return btoa(unescape(encodeURIComponent(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function base64UrlDecode(str) {
  const s = String(str).replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4 ? '='.repeat(4 - (s.length % 4)) : '';
  return decodeURIComponent(escape(atob(s + pad)));
}

/* ===========================
   Product fetching + caching
   =========================== */
const productCacheByHandle = {}; // handle -> product json
const variantCacheByUrl = {};    // url -> resolved variantId

async function fetchProductJsonByHandle(handle) {
  if (!handle) return null;
  if (productCacheByHandle[handle]) return productCacheByHandle[handle];

  const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
  if (!res.ok) return null;

  const pj = await res.json();
  productCacheByHandle[handle] = pj;
  return pj;
}

async function resolveVariantForUrl(url) {
  const u = normalizeUrl(url);
  if (!u) return null;
  if (variantCacheByUrl[u]) return variantCacheByUrl[u];

  const variantFromUrl = parseVariantFromUrl(u);
  const handle = getHandleFromUrl(u);
  const pj = await fetchProductJsonByHandle(handle);
  if (!pj) return null;

  const variants = pj.variants || [];
  let variant = null;

  if (variantFromUrl) {
    variant = variants.find(v => String(v.id) === String(variantFromUrl)) || null;
  }

  if (!variant) variant = variants[0] || null;
  if (!variant) return null;

  const out = {
    variantId: String(variant.id),
    title: pj.title || variant.name || 'Product',
    variantTitle: variant.public_title || variant.title || '',
    priceCents: variant.price, // cents
    image: (variant.featured_image && variant.featured_image.src) ? variant.featured_image.src : (pj.featured_image || ''),
    url: u,
    handle,
  };

  variantCacheByUrl[u] = out;
  return out;
}

/* ===========================
   State
   cartState: variantId -> item
   item = { variantId, qty, sectionId, url, title, variantTitle, priceCents, image }
   =========================== */
const cartState = {};
const DEFAULT_QTY = 1;

/* ===========================
   DOM IDs
   =========================== */
const ROOT_ID = 'radioConfiguratorRoot';

/* ===========================
   Build UI skeleton
   =========================== */
function ensureWidget() {
  const root = document.getElementById(ROOT_ID);
  if (!root) return null;

  root.innerHTML = `
    <div class="rc-card">
      <div class="rc-header">
        <div>
          <div class="rc-title">Build a Radio Package</div>
          <div class="rc-subtitle">Choose your main items, then fine-tune quantities and add extras in the results.</div>
        </div>
      </div>

      <div class="rc-grid" id="rcSelectGrid"></div>

      <div class="rc-actions">
        <button class="rc-btn rc-btn-primary" id="rcBuildBtn" type="button">Build package</button>
        <div class="rc-status" id="rcStatus"></div>
      </div>
    </div>

    <div class="rc-card rc-card-results" id="rcResults" style="display:none;">
      <div class="rc-results-header">
        <div>
          <div class="rc-title">Your Package</div>
          <div class="rc-subtitle">Adjust quantities, add extra items, then add everything to cart or copy a checkout link for email.</div>
        </div>
        <div class="rc-results-buttons">
          <button class="rc-btn rc-btn-primary" id="rcAddAllBtn" type="button">Add Everything to Cart</button>
          <button class="rc-btn" id="rcCopyCartLinkBtn" type="button">Copy checkout link</button>
          <button class="rc-btn" id="rcCopyResultsLinkBtn" type="button">Copy results link</button>
        </div>
      </div>

      <div class="rc-summary" id="rcSummary"></div>

      <div class="rc-sections" id="rcSections"></div>

      <div class="rc-status" id="rcResultsStatus" style="margin-top:10px;"></div>
    </div>
  `;

  // Build dropdowns (top selectors)
  const grid = qs('#rcSelectGrid', root);
  RADIO_CONFIG.sections.forEach(section => {
    const block = document.createElement('div');
    block.className = 'rc-select-block';
    block.innerHTML = `
      <div class="rc-label">${section.title}</div>
      <div class="rc-helper">${section.helper || ''}</div>
      <select class="rc-select" id="rcSelect_${section.id}">
        ${section.options.map(o => `<option value="${(o.url || '').replace(/"/g,'&quot;')}">${o.label}</option>`).join('')}
      </select>
    `;
    grid.appendChild(block);
  });

  // Bind actions
  qs('#rcBuildBtn', root).onclick = async () => {
    await buildFromTopSelections();
  };

  qs('#rcAddAllBtn', root).onclick = async () => {
    await addEverythingToCart();
  };

  qs('#rcCopyCartLinkBtn', root).onclick = async () => {
    await copyCheckoutLink();
  };

  qs('#rcCopyResultsLinkBtn', root).onclick = async () => {
    await copyResultsLink();
  };

  return root;
}

/* ===========================
   Add/update state helpers
   =========================== */
function upsertItem(item) {
  if (!item || !item.variantId) return;
  const existing = cartState[item.variantId];
  if (existing) {
    existing.qty += item.qty;
  } else {
    cartState[item.variantId] = { ...item };
  }
}

function removeItem(variantId) {
  delete cartState[String(variantId)];
}

function setQty(variantId, qty) {
  const id = String(variantId);
  if (!cartState[id]) return;
  const q = Math.max(1, Number(qty) || 1);
  cartState[id].qty = q;
}

/* ===========================
   Build from top dropdown selections
   =========================== */
async function buildFromTopSelections() {
  const statusEl = document.getElementById('rcStatus');
  if (statusEl) statusEl.textContent = 'Building…';

  // Clear previous state for a clean “build”
  for (const k of Object.keys(cartState)) delete cartState[k];

  try {
    for (const section of RADIO_CONFIG.sections) {
      const sel = document.getElementById(`rcSelect_${section.id}`);
      const url = normalizeUrl(sel?.value);
      if (!url) continue;

      // ✅ OFCOM message (if they selected it)
      if (isOfcomLicenceUrl(url)) showOfcomNoticeOnce();

      const v = await resolveVariantForUrl(url);
      if (!v) continue;

      upsertItem({
        variantId: v.variantId,
        qty: DEFAULT_QTY,
        sectionId: section.id,
        url: v.url,
        title: v.title,
        variantTitle: v.variantTitle,
        priceCents: v.priceCents,
        image: v.image
      });
    }

    renderResults();
    if (statusEl) statusEl.textContent = '';
  } catch (e) {
    console.error(e);
    if (statusEl) statusEl.textContent = 'Something went wrong building the package. Check console.';
  }
}

/* ===========================
   Render Results
   =========================== */
function groupItemsBySection() {
  const grouped = {};
  RADIO_CONFIG.sections.forEach(s => (grouped[s.id] = []));
  // Any unknown section falls into accessories as a safe default
  grouped._other = [];

  for (const item of Object.values(cartState)) {
    const sec = item.sectionId && grouped[item.sectionId] ? item.sectionId : '_other';
    grouped[sec].push(item);
  }
  return grouped;
}

function calcTotals() {
  let qty = 0;
  let valueCents = 0;
  for (const item of Object.values(cartState)) {
    const q = Number(item.qty) || 0;
    qty += q;
    const unit = Number(item.priceCents) || 0;
    valueCents += unit * q;
  }
  return { qty, valueCents };
}

function renderResults() {
  const results = document.getElementById('rcResults');
  const sectionsEl = document.getElementById('rcSections');
  const summaryEl = document.getElementById('rcSummary');

  const hasItems = Object.keys(cartState).length > 0;
  if (!results || !sectionsEl || !summaryEl) return;

  results.style.display = hasItems ? '' : 'none';
  if (!hasItems) return;

  const { qty, valueCents } = calcTotals();

  summaryEl.innerHTML = `
    <div class="rc-summary-card">
      <div class="rc-summary-label">Total items</div>
      <div class="rc-summary-value">${qty}</div>
    </div>
    <div class="rc-summary-card">
      <div class="rc-summary-label">Estimated total</div>
      <div class="rc-summary-value">${moneyFromCents(valueCents)}</div>
      <div class="rc-summary-note">Prices pulled from Shopify product data (as shown on site).</div>
    </div>
  `;

  const grouped = groupItemsBySection();

  sectionsEl.innerHTML = RADIO_CONFIG.sections.map(section => {
    const items = grouped[section.id] || [];

    const rowsHtml = items.length
      ? items.map(renderItemRow).join('')
      : `<div class="rc-empty">No items selected in this section yet.</div>`;

    // “Add another…” controls for this section
    // ✅ Placeholder text as default selection
    const addAnother = `
      <div class="rc-add-another">
        <select class="rc-select rc-select-small" id="rcAddSelect_${section.id}">
          <option value="" selected disabled>Click here to select another product…</option>
          ${section.options
            .filter(o => normalizeUrl(o.url)) // remove Not applicable
            .map(o => `<option value="${(o.url || '').replace(/"/g,'&quot;')}">${o.label}</option>`)
            .join('')}
        </select>
        <button class="rc-btn rc-btn-primary" type="button" data-add-section="${section.id}">Add another</button>
      </div>
    `;

    return `
      <div class="rc-section">
        <div class="rc-section-title">${section.title}</div>
        <div class="rc-section-body">
          ${rowsHtml}
          ${addAnother}
        </div>
      </div>
    `;
  }).join('');

  // Bind “Add another” buttons
  sectionsEl.querySelectorAll('button[data-add-section]').forEach(btn => {
    btn.onclick = async () => {
      const sectionId = btn.getAttribute('data-add-section');
      await addAnotherFromSection(sectionId);
    };
  });

  // Bind qty steppers + remove
  sectionsEl.querySelectorAll('[data-qty-minus]').forEach(b => {
    b.onclick = () => {
      const id = b.getAttribute('data-qty-minus');
      const cur = cartState[id]?.qty || 1;
      setQty(id, cur - 1);
      renderResults();
    };
  });

  sectionsEl.querySelectorAll('[data-qty-plus]').forEach(b => {
    b.onclick = () => {
      const id = b.getAttribute('data-qty-plus');
      const cur = cartState[id]?.qty || 1;
      setQty(id, cur + 1);
      renderResults();
    };
  });

  sectionsEl.querySelectorAll('[data-qty-input]').forEach(inp => {
    inp.onchange = () => {
      const id = inp.getAttribute('data-qty-input');
      setQty(id, inp.value);
      renderResults();
    };
  });

  sectionsEl.querySelectorAll('[data-remove]').forEach(b => {
    b.onclick = () => {
      const id = b.getAttribute('data-remove');
      removeItem(id);
      renderResults();
    };
  });
}

function renderItemRow(item) {
  const id = String(item.variantId);
  const qty = Number(item.qty) || 1;
  const unit = Number(item.priceCents) || 0;
  const line = unit * qty;

  const img = item.image
    ? `<img class="rc-img" src="${item.image}" loading="lazy" alt="${(item.title || '').replace(/"/g,'')}">`
    : `<div class="rc-img rc-img-placeholder"></div>`;

  const titleLine = item.variantTitle ? `${item.title} <span class="rc-muted">(${item.variantTitle})</span>` : item.title;

  return `
    <div class="rc-row">
      ${img}
      <div class="rc-row-main">
        <a class="rc-link" href="${item.url}" target="_blank" rel="noopener">
          <div class="rc-item-title">${titleLine}</div>
        </a>
        <div class="rc-item-meta">
          <span class="rc-price">${moneyFromCents(unit)}</span>
          <span class="rc-muted">each</span>
          <span class="rc-dot">•</span>
          <span class="rc-price">${moneyFromCents(line)}</span>
          <span class="rc-muted">line</span>
        </div>
      </div>

      <div class="rc-row-controls">
        <button class="rc-qtybtn" type="button" data-qty-minus="${id}">−</button>
        <input class="rc-qty" type="number" min="1" value="${qty}" data-qty-input="${id}">
        <button class="rc-qtybtn" type="button" data-qty-plus="${id}">+</button>

        <button class="rc-remove" type="button" data-remove="${id}" title="Remove">✕</button>
      </div>
    </div>
  `;
}

/* ===========================
   Add another item from section
   =========================== */
async function addAnotherFromSection(sectionId) {
  const statusEl = document.getElementById('rcResultsStatus');
  if (statusEl) statusEl.textContent = 'Adding…';

  try {
    const sel = document.getElementById(`rcAddSelect_${sectionId}`);
    const url = normalizeUrl(sel?.value);
    if (!url) {
      if (statusEl) statusEl.textContent = 'No product selected.';
      return;
    }

    // ✅ OFCOM message (if they add it here)
    if (isOfcomLicenceUrl(url)) showOfcomNoticeOnce();

    const v = await resolveVariantForUrl(url);
    if (!v) {
      if (statusEl) statusEl.textContent = 'Could not load that product.';
      return;
    }

    upsertItem({
      variantId: v.variantId,
      qty: DEFAULT_QTY,
      sectionId,
      url: v.url,
      title: v.title,
      variantTitle: v.variantTitle,
      priceCents: v.priceCents,
      image: v.image
    });

    renderResults();
    if (statusEl) statusEl.textContent = '';
  } catch (e) {
    console.error(e);
    if (statusEl) statusEl.textContent = 'Could not add item. Check console.';
  }
}

/* ===========================
   Cart actions
   =========================== */
function setBusy(isBusy, msg = '') {
  const status = document.getElementById('rcResultsStatus');
  if (status) status.textContent = msg;

  const ids = ['rcAddAllBtn', 'rcCopyCartLinkBtn', 'rcCopyResultsLinkBtn'];
  ids.forEach(id => {
    const el = document.getElementById(id);
    if (el) el.disabled = isBusy;
  });
}

async function addEverythingToCart() {
  setBusy(true, 'Adding to cart…');

  try {
    const items = Object.values(cartState).map(i => ({
      id: Number(i.variantId),
      quantity: Number(i.qty) || 1
    }));

    if (!items.length) {
      setBusy(false, 'Nothing to add.');
      return;
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || 'Add to cart failed');
    }

    setBusy(false, 'Added ✅ Redirecting to cart…');
    window.location.href = '/cart';
  } catch (e) {
    console.error(e);
    setBusy(false, 'Could not add everything. Check console.');
  }
}

function buildPrefilledCartPath() {
  // /cart/variant:qty,variant:qty
  const parts = Object.values(cartState).map(i => `${i.variantId}:${Number(i.qty) || 1}`);
  return `/cart/${parts.join(',')}`;
}

async function copyCheckoutLink() {
  setBusy(true, 'Building checkout link…');

  try {
    const path = buildPrefilledCartPath();
    if (!path || path === '/cart/') {
      setBusy(false, 'Nothing selected.');
      return;
    }

    const url = `${window.location.origin}${path}`;
    const ok = await navigator.clipboard.writeText(url).then(() => true).catch(() => false);

    if (!ok) {
      prompt('Copy this link:', url);
      setBusy(false, 'Link ready (copied via prompt).');
      return;
    }

    setBusy(false, 'Checkout link copied ✅ (paste into your email)');
  } catch (e) {
    console.error(e);
    setBusy(false, 'Could not copy link. Check console.');
  }
}

/* ===========================
   Results link (reload selection)
   =========================== */
function serializeState() {
  // minimal payload
  const items = Object.values(cartState).map(i => ({
    v: i.variantId,
    q: Number(i.qty) || 1,
    s: i.sectionId,
    u: i.url
  }));

  return { v: 1, items, ts: Date.now() };
}

async function copyResultsLink() {
  setBusy(true, 'Building results link…');

  try {
    const payload = serializeState();
    const encoded = base64UrlEncode(JSON.stringify(payload));
    const url = `${window.location.origin}${window.location.pathname}?radio_pkg=${encoded}`;

    const ok = await navigator.clipboard.writeText(url).then(() => true).catch(() => false);
    if (!ok) {
      prompt('Copy this link:', url);
      setBusy(false, 'Results link ready (copied via prompt).');
      return;
    }

    setBusy(false, 'Results link copied ✅');
  } catch (e) {
    console.error(e);
    setBusy(false, 'Could not copy results link. Check console.');
  }
}

async function tryAutoLoadFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const pkg = params.get('radio_pkg');
  if (!pkg) return;

  try {
    const payload = JSON.parse(base64UrlDecode(pkg));
    const items = Array.isArray(payload?.items) ? payload.items : [];
    if (!items.length) return;

    // Clear state
    for (const k of Object.keys(cartState)) delete cartState[k];

    // Rebuild from urls (ensures correct current price/image/variant)
    for (const it of items) {
      const url = normalizeUrl(it.u);
      if (!url) continue;

      // ✅ OFCOM message (if it exists in results link)
      if (isOfcomLicenceUrl(url)) showOfcomNoticeOnce();

      const v = await resolveVariantForUrl(url);
      if (!v) continue;

      upsertItem({
        variantId: v.variantId,
        qty: Math.max(1, Number(it.q) || 1),
        sectionId: it.s || 'accessories',
        url: v.url,
        title: v.title,
        variantTitle: v.variantTitle,
        priceCents: v.priceCents,
        image: v.image
      });
    }

    renderResults();
  } catch (e) {
    console.warn('Invalid radio_pkg link', e);
  }
}

/* ===========================
   Boot
   =========================== */
document.addEventListener('DOMContentLoaded', async () => {
  const root = ensureWidget();
  if (!root) return;

  await tryAutoLoadFromUrl();
});

